// EK-A21 — the one place that turns a `stock_layer_missing` anomaly into text.
// Used by PODetail ("PO Anomalies") and the Ops view, so the two cannot
// drift apart (Rule-16 review of #1570).

export const STOCK_LAYER_MISSING_LABEL = 'Done without FIFO layer'

// Rule-16 re-review of #1570: no promise of a backfill that does not exist (O38 is
// open, no tool creates missing PO layers) — and no invitation to dismiss for good.
export const STOCK_LAYER_MISSING_NEXT_STEP =
  'The cost of these lines is missing from the stock valuation. Correcting the price ' +
  'does not add it — leave this open until the FIFO layer has been added.'

const MAX_SKUS = 5

// Returns { summary, skus } for an anomaly's `details`. Robust against old or
// partial rows: missing fields never render as "undefined" or "0 of  lines".
export function describeStockLayerMissing(details) {
  const d = details && typeof details === 'object' ? details : {}
  const missing = Array.isArray(d.lines_missing) ? d.lines_missing.length : 0
  const expected = Number.isInteger(d.lines_expected) ? d.lines_expected : null
  const priceZero = Array.isArray(d.lines_price_zero) ? d.lines_price_zero.length : 0
  const unreadableIdx = Array.isArray(d.lines_unreadable) ? d.lines_unreadable : []
  const unreadable = unreadableIdx.length
  const hints = d.sku_hint && typeof d.sku_hint === 'object' ? d.sku_hint : {}
  const word = (x) => (x === 1 ? 'line' : 'lines')

  let summary = null
  if (expected !== null && missing > 0) {
    summary = `${missing} of ${expected} ${word(expected)} ${missing === 1 ? 'has' : 'have'} no FIFO layer`
  } else if (missing > 0) {
    summary = `${missing} ${word(missing)} without FIFO layer`
  }
  // N5 (Folgebahn): an unreadable line usually stops the layer push for ALL lines of
  // the PO, so it comes first and names its own SKU (Rule-16 review of #1572, U1/U2).
  // The list of ALL missing SKUs is NOT appended to the summary — the views show it on
  // its own labelled line ("SKUs: …"); appended with ":" it read as belonging to the
  // last clause (Rule-16 re-review of #1572, W1). No causal sentence: a line unreadable
  // today may have lacked its layer for another reason back then.
  if (summary && unreadable > 0) {
    const own = unreadableIdx.map(i => hints[String(i)]).filter(Boolean)
    let ownText = own.slice(0, MAX_SKUS).join(', ')
    if (own.length > MAX_SKUS) ownText += ` +${own.length - MAX_SKUS} more`
    summary += ` · ${unreadable} unreadable ${word(unreadable)}${ownText ? ` (${ownText})` : ''}`
  }
  if (summary && priceZero > 0) summary += ` · ${priceZero} with price 0`

  const all = Object.values(hints).filter(Boolean)
  let skus = all.slice(0, MAX_SKUS).join(', ')
  if (all.length > MAX_SKUS) skus += ` +${all.length - MAX_SKUS} more`

  return { summary, skus: skus || null }
}
